import React, { useState, useRef } from "react";
import {
    Box,
    Button,
    Popover,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    CircularProgress,
    Typography,
    InputBase,
    Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import masterDataService, { TagDataDTO } from "../services/masterDataService";


interface DietaryRestrictionsDropdownProps {
    onAdd: (tagValue: string) => void;
    onRemove: (tagValue: string) => void;
    disabled?: boolean;
}

const DietaryRestrictionsDropdown: React.FC<DietaryRestrictionsDropdownProps> = ({ onAdd, onRemove, disabled }) => {
    const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
    const [availableTags, setAvailableTags] = useState<TagDataDTO[]>([]);
    const [tagsAnchorEl, setTagsAnchorEl] = useState<HTMLElement | null>(null);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [tagSearch, setTagSearch] = useState("");
    const tagsLoadedRef = useRef(false);
    const tagsOpen = Boolean(tagsAnchorEl);

    const fetchTags = async () => {
        if (tagsLoadedRef.current) return;
        setTagsLoading(true);
        try {
            const tags = await masterDataService.getTagsByCategoryName("Dietary Restrictions");
            setAvailableTags(tags.filter((t: TagDataDTO) => t.isActive !== false));
            tagsLoadedRef.current = true;
        } catch (err) {
            console.error("Failed to fetch dietary restrictions tags:", err);
        } finally {
            setTagsLoading(false);
        }
    };

    const handleOpenTagsDropdown = (event: React.MouseEvent<HTMLElement>) => {
        setTagsAnchorEl(event.currentTarget);
        fetchTags();
    };

    const handleCloseTagsDropdown = () => {
        setTagsAnchorEl(null);
        setTagSearch("");
    };

    const handleTagToggle = (tag: TagDataDTO) => {
        const newSelected = new Set(selectedTagIds);
        if (newSelected.has(tag.id)) {
            newSelected.delete(tag.id);
            onRemove(tag.fieldValue);
        } else {
            newSelected.add(tag.id);
            onAdd(tag.fieldValue);
        }
        setSelectedTagIds(newSelected);
    };

    const filteredTags = availableTags.filter((t) =>
        t.fieldValue.toLowerCase().includes(tagSearch.toLowerCase())
    );

    return (
        <Box>
            <Button
                variant="outlined"
                size="small"
                endIcon={tagsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                onClick={handleOpenTagsDropdown}
                disabled={disabled}
                sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 500,
                    color: "text.secondary",
                    borderColor: "divider",
                    "&:hover": { borderColor: "primary.main", color: "primary.main" },
                    width: 250,
                    justifyContent: "space-between",
                }}
            >
                Add Dietary Restrictions
            </Button>

            <Popover
                open={tagsOpen}
                anchorEl={tagsAnchorEl}
                onClose={handleCloseTagsDropdown}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                    sx: {
                        mt: 0.5,
                        width: 500,
                        borderRadius: 2,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                        overflow: "hidden",
                    },
                }}
            >
                <Box sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    <InputBase
                        placeholder="Search tags…"
                        value={tagSearch}
                        onChange={(e) => setTagSearch(e.target.value)}
                        autoFocus
                        fullWidth
                        sx={{ fontSize: "0.875rem" }}
                    />
                </Box>
                <Divider />

                {tagsLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                ) : filteredTags.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 2, textAlign: "center" }}>
                        {tagSearch ? "No matching tags." : "No tags available."}
                    </Typography>
                ) : (
                    <List dense disablePadding sx={{ maxHeight: 240, overflowY: "auto" }}>
                        {filteredTags.map((tag) => {
                            const isSelected = selectedTagIds.has(tag.id);
                            return (
                                <ListItem
                                    key={tag.id}
                                    button
                                    onClick={() => handleTagToggle(tag)}
                                    selected={isSelected}
                                    sx={{
                                        py: 0.5,
                                        "&.Mui-selected": {
                                            bgcolor: "primary.50",
                                        },
                                        "&:hover": { bgcolor: "action.hover" },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <Checkbox
                                            edge="start"
                                            checked={isSelected}
                                            size="small"
                                            disableRipple
                                            color="primary"
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={tag.fieldValue}
                                        primaryTypographyProps={{ 
                                            variant: "body2",
                                            fontWeight: isSelected ? 600 : 400,
                                        }}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </Popover>
        </Box>
    );
};

export default DietaryRestrictionsDropdown;
